import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import api, { trackActivity, marketDataAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ChartPackApp.css';

// Drummond 2026 palette (CLAUDE.md), in fixed assignment order so a region keeps its
// colour across every chart in a section.
const C = {
  navy: '#012341', teal: '#337B91', slate: '#5E819C', tealDark: '#12706C', sage: '#A6C6C1',
  mauve: '#AA8090', lightBlue: '#BBCBD7', grey: '#3F4A59', greyMid: '#7F7F7F', amber: '#FBAE40', coral: '#F26B43',
};
const SERIES_COLOURS = [C.navy, C.teal, C.mauve, C.sage, C.tealDark, C.amber, C.slate, C.lightBlue, C.coral, C.greyMid];
const WINDOWS = [[1, '1y'], [3, '3y'], [5, '5y'], [10, '10y'], [20, '20y'], [0, 'Max']];

const fmtVal = (v, yFormat) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  if (yFormat === 'pct') return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
  if (yFormat === 'index') return v.toFixed(1);
  if (yFormat === 'number') return v.toFixed(1);
  return v.toFixed(2);
};
const fmtNum = (v) => {
  const n = Number(Number(v).toPrecision(10));           // strip float noise (2.9999997 -> 3)
  const abs = Math.abs(n);
  const dp = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return n.toLocaleString('en-AU', { maximumFractionDigits: dp });
};
const fmtTick = (v, yFormat) => (yFormat === 'pct' ? `${fmtNum(v)}%` : fmtNum(v));
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d) ? iso : d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

function ChartTooltip({ active, payload, label, yFormat, yFormatRight, rightKeys }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="cp-tooltip">
      <div className="cp-tooltip-label">{label}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.dataKey} className="cp-tooltip-row">
          <span className="cp-swatch" style={{ background: p.color }} />
          <span className="cp-tooltip-name">{p.name}</span>
          <span className="cp-tooltip-val">{fmtVal(p.value, rightKeys?.has(p.dataKey) ? (yFormatRight || yFormat) : yFormat)}</span>
        </div>
      ))}
    </div>
  );
}

// Click a legend entry to hide / show that series; hidden entries are greyed out.
function useLegendToggle(initialHidden = []) {
  const [hidden, setHidden] = useState(() => new Set(initialHidden));
  const toggle = (e) => {
    const key = e?.dataKey ?? e?.value;
    if (key == null) return;
    setHidden((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };
  const legendProps = {
    onClick: toggle,
    wrapperStyle: { fontSize: 11, cursor: 'pointer' },
    formatter: (value, entry) => (
      <span style={{ color: hidden.has(entry?.dataKey) ? '#BBCBD7' : '#3F4A59', textDecoration: hidden.has(entry?.dataKey) ? 'line-through' : 'none' }}>
        {value}
      </span>
    ),
  };
  return { hidden, legendProps };
}

// Robust y-axis bounds: values beyond 3 interquartile ranges of the plotted data are
// treated as outliers (a COVID spike in claims) and the axis stops at the fence so the
// rest of the history stays readable; the card offers a toggle to show the full range.
const quantile = (sorted, q) => {
  if (!sorted.length) return null;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos); const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};
const robustDomain = (values) => {
  const v = values.filter((x) => x != null && Number.isFinite(x)).sort((a, b) => a - b);
  if (v.length < 20) return { domain: ['auto', 'auto'], clipped: false };
  const q1 = quantile(v, 0.25); const q3 = quantile(v, 0.75); const iqr = q3 - q1;
  if (!(iqr > 0)) return { domain: ['auto', 'auto'], clipped: false };
  const loFence = q1 - 3 * iqr; const hiFence = q3 + 3 * iqr;
  const min = v[0]; const max = v[v.length - 1];
  const lo = Math.max(min, loFence); const hi = Math.min(max, hiFence);
  const clipped = lo > min || hi < max;
  if (!clipped) return { domain: ['auto', 'auto'], clipped: false };
  const pad = (hi - lo) * 0.05;
  return { ...niceAxis(lo - pad, hi + pad), clipped: true };
};

// Round a range out to a 1/2/2.5/5 x 10^k step and return the matching tick values, so a
// clipped axis reads 3, 4, 5 rather than 3.245, 4.145.
const niceAxis = (lo, hi) => {
  const span = hi - lo || Math.abs(hi) || 1;
  const rough = span / 5;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let t = start; t <= end + step / 2; t += step) ticks.push(Number(t.toPrecision(10)));
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
};

// Line chart: data = {dates, series:[{key,label,values}]} -> one row per date. Series listed in
// chart.right_axis are drawn against a right-hand axis with its own units.
function LinePanel({ chart, showOutliers }) {
  const { data, y_format: yFormat } = chart;
  const rightKeys = useMemo(() => new Set(chart.right_axis || []), [chart.right_axis]);
  const yFormatRight = chart.y_format_right || yFormat;
  // Finished fiscal years (expired=true from the API) start hidden; the legend shows them back.
  const { hidden, legendProps } = useLegendToggle(data.series.filter((s) => s.expired).map((s) => s.key));
  const rows = useMemo(() => data.dates.map((d, i) => {
    const r = { date: fmtDate(d) };
    data.series.forEach((s) => { r[s.key] = s.values[i]; });
    return r;
  }), [data]);
  const domains = useMemo(() => {
    const vis = data.series.filter((s) => !hidden.has(s.key));
    const left = robustDomain(vis.filter((s) => !rightKeys.has(s.key)).flatMap((s) => s.values));
    const right = robustDomain(vis.filter((s) => rightKeys.has(s.key)).flatMap((s) => s.values));
    return { left, right };
  }, [data, hidden, rightKeys]);
  const clip = !showOutliers;
  const hasRight = data.series.some((s) => rightKeys.has(s.key));
  const hasZero = yFormat === 'pct' && !chart.ref_lines?.length;
  return (
    <ResponsiveContainer width="100%" height={290}>
      <LineChart data={rows} margin={{ left: 0, right: hasRight ? 0 : 16, top: 8, bottom: 4 }}>
        <CartesianGrid stroke="#eef1f4" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
        <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => fmtTick(v, yFormat)}
               domain={clip && domains.left.clipped ? domains.left.domain : ['auto', 'auto']}
               ticks={clip && domains.left.clipped ? domains.left.ticks : undefined} allowDataOverflow={clip && domains.left.clipped} />
        {hasRight && (
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => fmtTick(v, yFormatRight)}
                 domain={clip && domains.right.clipped ? domains.right.domain : ['auto', 'auto']}
                 ticks={clip && domains.right.clipped ? domains.right.ticks : undefined} allowDataOverflow={clip && domains.right.clipped} />
        )}
        <Tooltip content={<ChartTooltip yFormat={yFormat} yFormatRight={yFormatRight} rightKeys={rightKeys} />} />
        <Legend {...legendProps} iconType="plainline" />
        {(chart.ref_lines?.length ? chart.ref_lines : hasZero ? [0] : []).map((v) => (
          <ReferenceLine key={v} yAxisId="left" y={v} stroke={C.grey} strokeWidth={1} strokeDasharray={v === 0 ? undefined : '4 3'} />
        ))}
        {data.series.map((s, i) => (
          <Line key={s.key} type="linear" dataKey={s.key} name={s.label} yAxisId={rightKeys.has(s.key) ? 'right' : 'left'}
                stroke={SERIES_COLOURS[i % SERIES_COLOURS.length]} strokeDasharray={rightKeys.has(s.key) ? '6 3' : undefined}
                dot={false} strokeWidth={s.key === 'world' ? 2.4 : 1.6} connectNulls={chart.freq === 'W'} isAnimationActive={false}
                hide={hidden.has(s.key)} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// Whether a chart's plotted values contain outliers the robust axis would cut.
const hasOutliers = (chart) => {
  if (chart.kind !== 'line' || !chart.data?.series) return false;
  const rightKeys = new Set(chart.right_axis || []);
  const left = chart.data.series.filter((s) => !rightKeys.has(s.key)).flatMap((s) => s.values);
  const right = chart.data.series.filter((s) => rightKeys.has(s.key)).flatMap((s) => s.values);
  return robustDomain(left).clipped || robustDomain(right).clipped;
};

// Bar chart: data = {categories, series:[{key,label,values}], as_of} -> grouped bars per category.
function BarPanel({ chart }) {
  const { data, y_format: yFormat } = chart;
  const { hidden, legendProps } = useLegendToggle();
  const rows = useMemo(() => data.categories.map((c, i) => {
    const r = { category: c };
    data.series.forEach((s) => { r[s.key] = s.values[i]; });
    return r;
  }), [data]);
  return (
    <ResponsiveContainer width="100%" height={290}>
      <BarChart data={rows} margin={{ left: 0, right: 16, top: 8, bottom: 4 }} barCategoryGap="22%" barGap={2}>
        <CartesianGrid stroke="#eef1f4" vertical={false} />
        <XAxis dataKey="category" tick={{ fontSize: 11 }} interval={0} />
        <YAxis tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => fmtTick(v, yFormat)} domain={['auto', 'auto']} />
        <Tooltip content={<ChartTooltip yFormat={yFormat} />} cursor={{ fill: '#f2f3f2' }} />
        <Legend {...legendProps} />
        <ReferenceLine y={0} stroke={C.grey} strokeWidth={1} />
        {data.series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={SERIES_COLOURS[i % SERIES_COLOURS.length]} isAnimationActive={false}
               hide={hidden.has(s.key)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ChartCard({ chart, years, onRemove, onHide, onRestore }) {
  const { data } = chart;
  const [showOutliers, setShowOutliers] = useState(false);
  const outliers = useMemo(() => hasOutliers(chart), [chart]);
  const empty = !data || data.error
    || (chart.kind === 'line' && (!data.dates?.length || !data.series?.some((s) => s.values.some((v) => v != null))))
    || (chart.kind === 'bar' && !data.series?.some((s) => s.values.some((v) => v != null)));
  return (
    <div className={`cp-card${chart.hidden ? ' cp-card-hidden' : ''}`}>
      <div className="cp-card-head">
        <h3>{chart.title}</h3>
        <span className="cp-card-window">
          {chart.kind === 'bar' && data?.as_of ? `as at ${fmtDate(data.as_of)}`
            : chart.windowed ? `${years ? `last ${years}y` : 'full history'}${chart.freq === 'W' ? ', weekly' : chart.freq === 'D' ? ', daily' : ''}` : ''}
        </span>
      </div>
      {chart.subtitle && <p className="cp-card-sub">{chart.subtitle}</p>}
      {data?.error ? (
        <div className="cp-alert">{data.error}</div>
      ) : empty ? (
        <div className="cp-empty">No data for this chart.</div>
      ) : chart.kind === 'bar' ? <BarPanel chart={chart} /> : <LinePanel chart={chart} showOutliers={showOutliers} />}
      {outliers && (
        <div className="cp-card-tools">
          <button type="button" className={`cp-chip${showOutliers ? ' active' : ''}`} onClick={() => setShowOutliers((v) => !v)}
                  title="The axis is clipped at 3 interquartile ranges so an extreme spike does not flatten the rest of the history">
            {showOutliers ? 'Hide outliers' : 'Show outliers'}
          </button>
          {!showOutliers && <span className="cp-muted">axis clipped at the outlier fence</span>}
        </div>
      )}
      {chart.user_chart ? (
        <div className="cp-card-tools">
          <span className="cp-chip">From Market and Economic Data</span>
          <button type="button" className="cp-chip" onClick={() => onRemove(chart.config_id)}
                  title="Take this chart out of the Chart Pack (the saved chart itself is kept)">Remove from Chart Pack</button>
        </div>
      ) : chart.hidden ? (
        <div className="cp-card-tools">
          <span className="cp-muted">Hidden{chart.hidden_by ? ` by ${chart.hidden_by}` : ''}{chart.hidden_at ? ` on ${chart.hidden_at}` : ''}</span>
          <button type="button" className="cp-chip" onClick={() => onRestore(chart.id)} title="Put this chart back for everyone">Restore</button>
        </div>
      ) : (
        <div className="cp-card-tools cp-card-tools-quiet">
          <button type="button" className="cp-chip" onClick={() => onHide(chart.id)}
                  title="Hide this chart from the Chart Pack for everyone (it can be restored from the hidden list)">Hide</button>
        </div>
      )}
      {chart.note && <div className="cp-card-note">{chart.note}</div>}
    </div>
  );
}

export default function ChartPackApp() {
  const { user } = useAuth();
  const [sections, setSections] = useState(null);
  const [catError, setCatError] = useState(null);
  const [active, setActive] = useState('earnings_revisions');
  const [years, setYears] = useState(10);
  const [payload, setPayload] = useState({});     // {`section|years`: {years, as_of, charts}}
  const [errors, setErrors] = useState({});       // {`section|years`: message}
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    trackActivity('app_access', 'chart_pack');
    api.get('/api/chart-pack/catalogue')
      .then((r) => setSections(r.data.sections))
      .catch((e) => setCatError(e.response?.data?.detail || e.message));
  }, []);

  // Fetch the active section for the chosen window; cached per section+window, and an
  // error is kept per key so switching tabs never shows a stale message.
  const cacheKey = `${active}|${years}`;
  useEffect(() => {
    if (!sections || payload[cacheKey]) return;
    let cancelled = false;
    api.get(`/api/chart-pack/section/${active}`, { params: { years } })
      .then((r) => {
        if (cancelled) return;
        setPayload((p) => ({ ...p, [cacheKey]: r.data }));
        setErrors((e) => ({ ...e, [cacheKey]: null }));
      })
      .catch((e) => { if (!cancelled) setErrors((prev) => ({ ...prev, [cacheKey]: e.response?.data?.detail || e.message })); });
    return () => { cancelled = true; };
  }, [sections, active, years, cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshRail = () => api.get('/api/chart-pack/catalogue').then((r) => setSections(r.data.sections)).catch(() => {});
  const patchChart = (chartId, patch) => setPayload((p) => {
    const cur = p[cacheKey];
    if (!cur) return p;
    return { ...p, [cacheKey]: { ...cur, charts: cur.charts.map((c) => (c.id === chartId ? { ...c, ...patch } : c)) } };
  });
  const hideChart = (chartId) => {
    api.put(`/api/chart-pack/hidden/${encodeURIComponent(chartId)}`)
      .then(() => { patchChart(chartId, { hidden: true, hidden_by: user?.email || null, hidden_at: new Date().toISOString().slice(0, 10) }); refreshRail(); })
      .catch((e) => setErrors((prev) => ({ ...prev, [cacheKey]: e.response?.data?.detail || e.message })));
  };
  const restoreChart = (chartId) => {
    api.delete(`/api/chart-pack/hidden/${encodeURIComponent(chartId)}`)
      .then(() => { patchChart(chartId, { hidden: false, hidden_by: null, hidden_at: null }); refreshRail(); })
      .catch((e) => setErrors((prev) => ({ ...prev, [cacheKey]: e.response?.data?.detail || e.message })));
  };

  const removeFromPack = (configId) => {
    marketDataAPI.updateChartConfig(configId, { chart_pack_section: null })
      .then(() => {
        // Drop every cached window of this section and refresh the rail counts.
        setPayload((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !k.startsWith(`${active}|`))));
        api.get('/api/chart-pack/catalogue').then((r) => setSections(r.data.sections)).catch(() => {});
      })
      .catch((e) => setErrors((prev) => ({ ...prev, [cacheKey]: e.response?.data?.detail || e.message })));
  };

  const section = sections?.find((s) => s.id === active);
  const current = payload[cacheKey];
  const error = errors[cacheKey];
  const loading = !!section && (section.charts.length > 0 || section.user_charts > 0) && !current && !error;
  const asOf = current?.as_of || {};

  return (
    <div className="cp-wrap">
      <nav className="cp-rail" aria-label="Chart pack sections">
        <div className="cp-rail-title">Chart Pack</div>
        {(sections || []).map((s) => (
          <button key={s.id} className={`cp-rail-tab${s.id === active ? ' active' : ''}`}
                  onClick={() => setActive(s.id)} title={s.label}>
            <span className="cp-rail-icon" aria-hidden="true">{s.icon}</span>
            <span className="cp-rail-label">{s.label}</span>
            <span className="cp-rail-count">{(s.charts.length - (s.hidden || 0) + (s.user_charts || 0)) || ''}</span>
          </button>
        ))}
        {catError && <div className="cp-alert">{catError}</div>}
      </nav>

      <section className="cp-main">
        <div className="cp-head">
          <div>
            <h1>{section?.label || 'Chart Pack'}</h1>
            <div className="cp-muted">
              Pre-built market and economic charts from the index returns tables.
              {asOf.monthly && <> Monthly data to {fmtDate(asOf.monthly)}.</>}
              {asOf.daily && <> Daily data to {asOf.daily}.</>}
            </div>
          </div>
          <div className="cp-toolbar">
            {(current?.charts?.some((c) => c.hidden)) && (
              <button type="button" className={`cp-chip${showHidden ? ' active' : ''}`} onClick={() => setShowHidden((v) => !v)}
                      title="Hidden charts are hidden for everyone; show them here to restore one">
                {showHidden ? 'Showing hidden' : `Show hidden (${current.charts.filter((c) => c.hidden).length})`}
              </button>
            )}
            <span className="cp-field-label">Window</span>
            <div className="cp-window">
              {WINDOWS.map(([y, label]) => (
                <button key={label} className={`cp-window-btn${years === y ? ' active' : ''}`} onClick={() => setYears(y)}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="cp-alert">{error}</div>}
        {!sections && !catError && <div className="cp-muted">Loading sections…</div>}
        {section && section.charts.length === 0 && !(section.user_charts > 0) && (
          <div className="cp-placeholder">No charts in {section.label} yet. Send one across from Market and Economic Data
            (Save Chart, then pick this section), or add a catalogue entry in <code>app/apps/chart_pack/catalogue.py</code>.</div>
        )}
        {section && (section.charts.length > 0 || section.user_charts > 0) && (
          loading && !current ? <div className="cp-muted">Loading charts…</div> : current ? (
            <div className="cp-grid">
              {current.charts.filter((c) => showHidden || !c.hidden).map((c) => (
                <ChartCard key={c.id} chart={c} years={current.years} onRemove={removeFromPack} onHide={hideChart} onRestore={restoreChart} />
              ))}
            </div>
          ) : null
        )}
      </section>
    </div>
  );
}
